import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  User, ArrowLeft, Mail, Phone, Calendar, Key, Battery, Wallet,
  Activity, Clock, Zap, MapPin, Building, FileText, Eye, Trash2, Car,
  AlertTriangle, X, Banknote, IdCard, Image, ShieldCheck, ShieldX, ExternalLink, Pencil,
  QrCode, Scan, Globe, Smartphone, Download, TrendingUp, CheckCircle, UserCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { formatDate, formatCurrency, extractApiErrorMessage } from '../../lib/utils';
import { Badge } from '../../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { InvoicePopup } from '../../components/sessions';
import { saveElementAsPdf } from '../../lib/pdf';

const COMPANY_INFO = {
  name: 'C.S.M Sh.p.k.',
  address: 'Tirane Vore Muçaj Rruga dytesore, Tirane -Vore, 1032, Vore, Tirane',
  city: 'Tiranë, Shqipëri',
  nipt: 'L91234567A',
  phone: '+355 68 200 1818',
  email: 'info@albmove.al',
};

const getWalletSummary = (client) => {
  const summary = client?.wallet_summary || {};
  const total = Number(client?.wallet_balance ?? summary.total_balance ?? 0);
  const globalBalance = Number(summary.global_balance ?? total ?? 0);
  const stationBalance = Number(summary.station_balance ?? 0);
  const restrictedWallets = Array.isArray(summary.restricted_wallets) ? summary.restricted_wallets : [];

  return {
    total,
    globalBalance,
    stationBalance,
    restrictedWallets,
  };
};

// QR Logs Section Component
const QRLogsSection = ({ clientId }) => {
  const [logs, setLogs] = useState([]);
  const [qrHistory, setQrHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('scans'); // 'scans' or 'history'
  
  useEffect(() => {
    const fetchQRData = async () => {
      try {
        setLoading(true);
        const [logsRes, historyRes] = await Promise.all([
          api.get(`/admin/clients/${clientId}/qr-logs?limit=100`),
          api.get(`/admin/clients/${clientId}/qr-history?limit=50`)
        ]);
        setLogs(logsRes.data.logs || []);
        setQrHistory(historyRes.data.qr_codes || []);
      } catch (error) {
        console.error('Error fetching QR data:', error);
        toast.error('Gabim në ngarkimin e QR logs');
      } finally {
        setLoading(false);
      }
    };
    
    if (clientId) {
      fetchQRData();
    }
  }, [clientId]);
  
  const formatLocation = (location) => {
    if (!location) return '-';
    const lat = location.latitude?.toFixed(4);
    const lng = location.longitude?.toFixed(4);
    if (location.address) {
      return (
        <div>
          <span className="text-zinc-300">{location.address}</span>
          <div className="text-xs text-zinc-500">({lat}, {lng})</div>
        </div>
      );
    }
    return `${lat}, ${lng}`;
  };
  
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('sq-AL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };
  
  if (loading) {
    return (
      <Card className="bg-zinc-900/50 border-white/10">
        <CardContent className="py-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto mb-3"></div>
          <p className="text-zinc-400">Duke ngarkuar QR logs...</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Toggle between views */}
      <div className="flex gap-2">
        <Button
          variant={activeView === 'scans' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveView('scans')}
          className={activeView === 'scans' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
        >
          <Scan className="w-4 h-4 mr-2" />
          Skanime ({logs.length})
        </Button>
        <Button
          variant={activeView === 'history' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveView('history')}
          className={activeView === 'history' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
        >
          <QrCode className="w-4 h-4 mr-2" />
          Histori QR ({qrHistory.length})
        </Button>
      </div>
      
      {/* Scans View */}
      {activeView === 'scans' && (
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Scan className="w-5 h-5 text-emerald-500" />
              Skanime të QR Kodit
            </CardTitle>
            <CardDescription>
              Historiku i çdo skanimi të QR kodit të klientit për audit sigurie
            </CardDescription>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <div className="text-center py-8">
                <QrCode className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400">Nuk ka skanime të regjistruara</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-zinc-400">Data & Ora</TableHead>
                      <TableHead className="text-zinc-400">Pajisja/Charger</TableHead>
                      <TableHead className="text-zinc-400">Vendndodhja Gjenerimit</TableHead>
                      <TableHead className="text-zinc-400">Vendndodhja Skanimit</TableHead>
                      <TableHead className="text-zinc-400">IP</TableHead>
                      <TableHead className="text-zinc-400">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log, index) => (
                      <TableRow key={log.id || index} className="border-white/10 hover:bg-white/5">
                        <TableCell className="text-zinc-300 whitespace-nowrap">
                          {formatDateTime(log.scanned_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            {log.charger_id && (
                              <span className="text-emerald-400 flex items-center gap-1">
                                <Zap className="w-3 h-3" />
                                {log.charger_id}
                              </span>
                            )}
                            {log.device_id && (
                              <span className="text-blue-400 flex items-center gap-1">
                                <Smartphone className="w-3 h-3" />
                                {log.device_id}
                              </span>
                            )}
                            {log.scanner_id && !log.charger_id && !log.device_id && (
                              <span className="text-zinc-400">{log.scanner_id}</span>
                            )}
                            {!log.charger_id && !log.device_id && !log.scanner_id && (
                              <span className="text-zinc-500">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {log.generated_location ? (
                            <div className="flex items-start gap-1">
                              <MapPin className="w-3 h-3 text-blue-400 mt-1 flex-shrink-0" />
                              <span className="text-zinc-400 text-sm">
                                {formatLocation(log.generated_location)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-zinc-500">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {log.scanned_location ? (
                            <div className="flex items-start gap-1">
                              <MapPin className="w-3 h-3 text-emerald-400 mt-1 flex-shrink-0" />
                              <span className="text-zinc-300 text-sm">
                                {formatLocation(log.scanned_location)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-zinc-500">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-zinc-400 text-sm">
                          {log.ip_address || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={log.success ? 'success' : 'destructive'}
                            className={log.success ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}
                          >
                            {log.success ? 'Sukses' : 'Dështoi'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* History View */}
      {activeView === 'history' && (
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <QrCode className="w-5 h-5 text-blue-500" />
              Histori QR Kodesh
            </CardTitle>
            <CardDescription>
              Të gjithë QR kodet e gjeneruar nga ky klient
            </CardDescription>
          </CardHeader>
          <CardContent>
            {qrHistory.length === 0 ? (
              <div className="text-center py-8">
                <QrCode className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400">Nuk ka QR kode të gjeneruar</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-zinc-400">Krijuar</TableHead>
                      <TableHead className="text-zinc-400">Pajisja</TableHead>
                      <TableHead className="text-zinc-400">Vendndodhja</TableHead>
                      <TableHead className="text-zinc-400">Skanuar</TableHead>
                      <TableHead className="text-zinc-400">Nr. Skanime</TableHead>
                      <TableHead className="text-zinc-400">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {qrHistory.map((qr, index) => (
                      <TableRow key={qr.id || index} className="border-white/10 hover:bg-white/5">
                        <TableCell className="text-zinc-300 whitespace-nowrap">
                          {formatDateTime(qr.created_at)}
                        </TableCell>
                        <TableCell className="text-zinc-400 text-sm">
                          {qr.generated_device || '-'}
                        </TableCell>
                        <TableCell>
                          {qr.generated_location ? (
                            <div className="flex items-start gap-1">
                              <MapPin className="w-3 h-3 text-blue-400 mt-1 flex-shrink-0" />
                              <span className="text-zinc-400 text-sm">
                                {formatLocation(qr.generated_location)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-zinc-500">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-zinc-300 whitespace-nowrap">
                          {qr.scanned_at ? formatDateTime(qr.scanned_at) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="border-zinc-600">
                            {qr.scan_count || 0}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {qr.is_active ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400">Aktiv</Badge>
                          ) : qr.scanned ? (
                            <Badge className="bg-blue-500/20 text-blue-400">Skanuar</Badge>
                          ) : (
                            <Badge className="bg-zinc-500/20 text-zinc-400">Mbyllur</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isModerator } = useAuth();
  const isBusiness = user?.role === 'BUSINESS';
  
  const [client, setClient] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [rfidCards, setRfidCards] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleHistory, setVehicleHistory] = useState([]);
  const [vehicleHistoryLoading, setVehicleHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialogs
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [vehicleDetailOpen, setVehicleDetailOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  
  // Cash topup dialog
  const [topupDialogOpen, setTopupDialogOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupNotes, setTopupNotes] = useState('');
  const [topupSiteId, setTopupSiteId] = useState('');
  const [sites, setSites] = useState([]);
  const [topupLoading, setTopupLoading] = useState(false);

  // Document preview modal
  const [documentPreviewOpen, setDocumentPreviewOpen] = useState(false);
  const [previewDocumentUrl, setPreviewDocumentUrl] = useState('');
  const [previewDocumentTitle, setPreviewDocumentTitle] = useState('');
  
  // Edit profile dialog
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editProfileLoading, setEditProfileLoading] = useState(false);
  const [discountPercent, setDiscountPercent] = useState('');
  const [discountFixedLek, setDiscountFixedLek] = useState('');
  const [discountSaving, setDiscountSaving] = useState(false);
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoicePdfLoading, setInvoicePdfLoading] = useState(false);
  const [autoGeneratePdf, setAutoGeneratePdf] = useState(false);
  const invoiceRef = useRef(null);
  const [editProfileForm, setEditProfileForm] = useState({
    full_name: '',
    phone: '',
    country: '',
    city: '',
    address: '',
    id_card: '',
    nipt: '',
    business_name: ''
  });

  // Helper function to get verification status and missing documents
  const getVerificationStatus = (clientData) => {
    if (!clientData) return { isVerified: false, missing: [] };
    
    const missing = [];
    
    // Check OTP verification
    if (!clientData.is_verified && !clientData.email_verified) {
      missing.push('Email i pa verifikuar me OTP');
    }
    
    // Check ID Card
    if (!clientData.id_card && !clientData.id_card_front && !clientData.id_card_back) {
      missing.push('Karta e Identitetit mungon');
    }
    
    // Check NIPT for business accounts
    if (clientData.account_type === 'business' && !clientData.nipt) {
      missing.push('NIPT mungon (llogari biznesi)');
    }
    
    // Check phone
    if (!clientData.phone) {
      missing.push('Numri i telefonit mungon');
    }
    
    const isFullyVerified = missing.length === 0 && clientData.is_verified;
    
    return { isVerified: isFullyVerified, missing };
  };

  const openEditProfile = () => {
    setEditProfileForm({
      full_name: client.full_name || '',
      phone: client.phone || '',
      country: client.country || '',
      city: client.city || '',
      address: client.address || '',
      id_card: client.id_card || '',
      nipt: client.nipt || '',
      business_name: client.business_name || ''
    });
    setEditProfileOpen(true);
  };

  const handleSaveProfile = async () => {
    try {
      setEditProfileLoading(true);
      
      // Only send changed fields
      const updateData = {};
      if (editProfileForm.full_name !== client.full_name) updateData.full_name = editProfileForm.full_name;
      if (editProfileForm.phone !== client.phone) updateData.phone = editProfileForm.phone;
      if (editProfileForm.country !== client.country) updateData.country = editProfileForm.country;
      if (editProfileForm.city !== client.city) updateData.city = editProfileForm.city;
      if (editProfileForm.address !== client.address) updateData.address = editProfileForm.address;
      if (editProfileForm.id_card !== client.id_card) updateData.id_card = editProfileForm.id_card;
      if (editProfileForm.nipt !== client.nipt) updateData.nipt = editProfileForm.nipt;
      if (editProfileForm.business_name !== client.business_name) updateData.business_name = editProfileForm.business_name;
      
      if (Object.keys(updateData).length === 0) {
        toast.info('Asnjë ndryshim për të ruajtur');
        setEditProfileOpen(false);
        return;
      }
      
      await api.put(`/admin/clients/${client.id}`, updateData);
      toast.success('Profili u përditësua me sukses');
      setEditProfileOpen(false);
      
      // Update local state
      setClient(prev => ({ ...prev, ...updateData }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në ruajtjen e profilit');
    } finally {
      setEditProfileLoading(false);
    }
  };

  const fetchSites = useCallback(async () => {
    try {
      const response = await api.get('/sites');
      const siteItems = Array.isArray(response.data) ? response.data : response.data?.sites || [];
      const sortedSites = [...siteItems].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sq'));
      setSites(sortedSites);
      setTopupSiteId((prev) => prev || (sortedSites.length === 1 ? sortedSites[0].id : ''));
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  }, []);

  const handleCashTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!amount || amount <= 0) {
      toast.error('Vendos një shumë të vlefshme');
      return;
    }
    if (!topupSiteId) {
      toast.error('Zgjidh stacionin ku do të kreditohet balanca');
      return;
    }
    
    try {
      setTopupLoading(true);
      const response = await api.post('/wallet/cash-topup', {
        user_id: client.id,
        amount: amount,
        site_id: topupSiteId,
        notes: topupNotes || 'Cash mbushje nga admin'
      });
      toast.success(`Balanca u rrit me ${amount} Kredite. Balanca totale e re: ${response.data.total_display_balance} Kredite`);
      setTopupDialogOpen(false);
      setTopupAmount('');
      setTopupNotes('');
      // Refresh client data
      setClient(prev => ({ ...prev, wallet_balance: response.data.total_display_balance }));
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'Gabim në mbushjen e balancës'));
    } finally {
      setTopupLoading(false);
    }
  };

  const handleSaveDiscount = async () => {
    const rawValue = (discountPercent || '').toString().trim();
    const rawFixedValue = (discountFixedLek || '').toString().trim();

    let percentValue = null;
    let fixedValue = null;

    if (rawValue !== '') {
      const value = Number(rawValue);
      if (Number.isNaN(value) || value < 0 || value > 100) {
        toast.error('Zbritja me përqindje duhet të jetë nga 0 deri në 100');
        return;
      }
      percentValue = Number(value.toFixed(2));
    }

    if (rawFixedValue !== '') {
      const value = Number(rawFixedValue);
      if (Number.isNaN(value) || value < 0) {
        toast.error('Zbritja fikse duhet të jetë 0 ose më e madhe');
        return;
      }
      fixedValue = Number(value.toFixed(2));
    }

    try {
      setDiscountSaving(true);
      await api.put('/settings/client-discount', {
        client_id: id,
        discount_percent: percentValue,
        discount_fixed_lek: fixedValue,
      });
      setClient((prev) => ({
        ...prev,
        charging_discount_percent: percentValue,
        charging_discount_fixed_lek: fixedValue,
      }));
      setDiscountPercent(percentValue === null ? '' : String(percentValue));
      setDiscountFixedLek(fixedValue === null ? '' : String(fixedValue));
      toast.success('Zbritja u ruajt me sukses');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim gjatë ruajtjes së zbritjes');
    } finally {
      setDiscountSaving(false);
    }
  };

  const handleClearDiscount = async () => {
    try {
      setDiscountSaving(true);
      await api.put('/settings/client-discount', {
        client_id: id,
        discount_percent: null,
        discount_fixed_lek: null,
      });
      setClient((prev) => ({
        ...prev,
        charging_discount_percent: null,
        charging_discount_fixed_lek: null,
      }));
      setDiscountPercent('');
      setDiscountFixedLek('');
      toast.success('Zbritja u hoq');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim gjatë heqjes së zbritjes');
    } finally {
      setDiscountSaving(false);
    }
  };

  const fetchClientData = useCallback(async () => {
    try {
      // Fetch client details
      let clientResponse;
      if (isBusiness) {
        const employeeResponse = await api.get(`/v1/business/employees/${id}`);
        const emp = employeeResponse?.data?.employee || {};
        const summary = emp?.wallet_summary || {};
        const totalBalance = Number(summary.total_balance ?? emp.wallet_balance ?? 0);
        const globalBalance = Number(summary.global_balance ?? totalBalance ?? 0);
        const stationBalance = Number(summary.station_balance ?? 0);
        const restrictedWallets = Array.isArray(summary.restricted_wallets) ? summary.restricted_wallets : [];
        const normalized = {
          id: emp.employee_account_id || emp.employee_id || id,
          employee_record_id: emp.id || id,
          full_name: emp.employee_name || 'Pa emër',
          email: emp.employee_email || '-',
          phone: emp.employee_phone || '',
          account_type: 'individual',
          status: emp.status || 'active',
          wallet_balance: totalBalance,
          wallet_summary: {
            total_balance: totalBalance,
            global_balance: globalBalance,
            station_balance: stationBalance,
            restricted_wallets: restrictedWallets,
          },
          total_sessions: 0,
          total_energy_kwh: 0,
          total_spent_lek: Number(emp?.wallet?.spent || 0),
          transactions: Array.isArray(emp.transactions) ? emp.transactions : []
        };
        clientResponse = { data: normalized };
      } else if (isModerator()) {
        // Moderator fetches their own client
        const clientsResponse = await api.get(`/moderators/${user.id}/clients`);
        const foundClient = clientsResponse.data.items?.find(c => c.id === id);
        if (!foundClient) {
          toast.error('Klienti nuk u gjet');
          navigate(-1);
          return;
        }
        clientResponse = { data: foundClient };
      } else {
        // Admin fetches any client with full details including vehicles
        clientResponse = await api.get(`/admin/clients/${id}`);
      }
      
      setClient(clientResponse.data);
      const currentDiscount = clientResponse.data?.charging_discount_percent;
      setDiscountPercent(
        currentDiscount === null || currentDiscount === undefined ? '' : String(currentDiscount)
      );
      const currentFixedDiscount = clientResponse.data?.charging_discount_fixed_lek;
      setDiscountFixedLek(
        currentFixedDiscount === null || currentFixedDiscount === undefined ? '' : String(currentFixedDiscount)
      );

      // Fetch sessions/details depending on role
      if (isBusiness) {
        setSessions(clientResponse.data.transactions || []);
        setRfidCards([]);
        setInvoices([]);
        setVehicles([]);
      } else {
        try {
          const sessionsResponse = await api.get(`/clients/${id}/sessions`);
          setSessions(sessionsResponse.data.items || sessionsResponse.data || []);
        } catch (err) {
          console.log('Sessions endpoint not available');
          setSessions([]);
        }

        // Fetch client's RFID cards
        try {
          const rfidResponse = await api.get(`/admin/clients/${id}/rfid-cards`);
          setRfidCards(rfidResponse.data.cards || rfidResponse.data || []);
        } catch (err) {
          console.log('RFID cards endpoint not available');
          setRfidCards([]);
        }

        // Fetch client's invoices
        try {
          const invoicesResponse = await api.get(`/admin/clients/${id}/invoices`);
          setInvoices(invoicesResponse.data.invoices || invoicesResponse.data || []);
        } catch (err) {
          console.log('Invoices endpoint not available');
          setInvoices([]);
        }

        // Fetch client's vehicles
        try {
          const vehiclesResponse = await api.get(`/admin/clients/${id}/vehicles`);
          setVehicles(vehiclesResponse.data.vehicles || vehiclesResponse.data || []);
        } catch (err) {
          console.log('Vehicles endpoint not available');
          if (clientResponse.data.vehicles) {
            setVehicles(clientResponse.data.vehicles);
          } else {
            setVehicles([]);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch client:', error);
      toast.error('Gabim në ngarkimin e klientit');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [id, user?.id, user?.role, isModerator, isBusiness, navigate]);

  // Fetch vehicle history when vehicle is selected
  const fetchVehicleHistory = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setVehicleDetailOpen(true);
    setVehicleHistoryLoading(true);
    
    try {
      const response = await api.get(`/admin/vehicles/${vehicle.id}/history`);
      setVehicleHistory(response.data.sessions || []);
    } catch (err) {
      console.error('Failed to fetch vehicle history:', err);
      setVehicleHistory([]);
    } finally {
      setVehicleHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchClientData();
  }, [fetchClientData]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const handleResetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Fjalëkalimi duhet të ketë të paktën 6 karaktere');
      return;
    }
    try {
      if (isModerator()) {
        await api.post(`/moderators/${user.id}/clients/${id}/reset-password`, {
          new_password: newPassword
        });
      } else {
        await api.post(`/clients/${id}/reset-password`, {
          new_password: newPassword
        });
      }
      toast.success('Fjalëkalimi u ndryshua');
      setPasswordDialogOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim');
    }
  };

  const handleVerifyDocument = async (documentType, verified) => {
    try {
      await api.post(`/admin/clients/${id}/verify-document`, {
        document_type: documentType,
        verified: verified,
        rejection_reason: verified ? null : 'Dokumenti u refuzua nga administratori'
      });
      toast.success(verified ? 'Dokumenti u verifikua' : 'Dokumenti u refuzua');
      fetchClientData(); // Refresh client data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim gjatë verifikimit');
    }
  };

  const handleViewSession = (session) => {
    // Navigate to session detail or show modal
    navigate(`/sessions/${session.id}`);
  };

  const handleDeleteSession = async (session) => {
    if (!window.confirm(`Jeni të sigurt që dëshironi të fshini sesionin e datës ${formatDate(session.start_time)}?`)) {
      return;
    }
    
    try {
      await api.delete(`/admin/sessions/${session.id}`);
      toast.success('Sesioni u fshi me sukses');
      // Refresh sessions
      const sessionsResponse = await api.get(`/clients/${id}/sessions`);
      setSessions(sessionsResponse.data.items || sessionsResponse.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim gjatë fshirjes');
    }
  };

  const normalizeInvoiceForPreview = (source) => {
    if (!source) return null;
    return {
      ...source,
      id: source.id || source.invoice_id || source._id,
      invoice_number: source.invoice_number,
      created_at: source.created_at || source.end_time || source.start_time,
      start_time: source.start_time,
      amount: source.amount ?? source.cost_lek ?? 0,
      cost_lek: source.cost_lek ?? source.amount ?? 0,
      type: source.type || 'charging',
      status: source.status,
      client_name: source.client_name || source.user_name || client?.full_name,
      client_email: source.client_email || source.user_email || client?.email,
      user_name: source.user_name || client?.full_name,
      user_email: source.user_email || client?.email,
      payment_method: source.payment_method,
      license_plate: source.license_plate,
      charger_id: source.charger_id,
    };
  };

  const generateInvoiceNumber = (invoice) => {
    if (!invoice) return 'N/A';
    if (invoice.invoice_number) return invoice.invoice_number;
    const sourceDate = invoice.created_at || invoice.start_time || new Date().toISOString();
    const date = new Date(sourceDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const idPrefix = String(invoice.id || 'XXXXXX').slice(0, 6).toUpperCase();
    return `ALB-${year}${month}-${idPrefix}`;
  };

  const formatEuropeanDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'N/A';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const openInvoicePreview = (rawInvoice, shouldDownload = false) => {
    const normalized = normalizeInvoiceForPreview(rawInvoice);
    if (!normalized) {
      toast.error('Fatura nuk është e vlefshme');
      return;
    }
    setSelectedInvoice(normalized);
    setInvoicePreviewOpen(true);
    setAutoGeneratePdf(shouldDownload);
  };

  const handleGenerateInvoicePDF = async () => {
    if (!invoiceRef.current || !selectedInvoice) return;
    try {
      setInvoicePdfLoading(true);
      await saveElementAsPdf(invoiceRef.current, `Fatura-${generateInvoiceNumber(selectedInvoice)}.pdf`);
      toast.success('Fatura u shkarkua me sukses');
    } catch (error) {
      toast.error('Gabim në gjenerimin e PDF');
    } finally {
      setInvoicePdfLoading(false);
    }
  };

  const handleViewInvoice = (session) => {
    if (!session) {
      toast.info('Fatura nuk është e lidhur me këtë sesion');
      return;
    }
    openInvoicePreview(session, false);
  };

  const handleDownloadInvoice = (session) => {
    if (!session) {
      toast.info('Fatura nuk është e lidhur me këtë sesion');
      return;
    }
    openInvoicePreview(session, true);
  };

  const handleViewClientInvoice = (invoice) => {
    openInvoicePreview(invoice, false);
  };

  const handleDownloadClientInvoice = (invoice) => {
    openInvoicePreview(invoice, true);
  };

  const getInvoiceStatusLabel = (invoiceOrStatus) => {
    const status = typeof invoiceOrStatus === 'object' ? invoiceOrStatus?.status : invoiceOrStatus;
    if (!status) {
      if (typeof invoiceOrStatus === 'object') {
        const type = String(invoiceOrStatus?.type || '').toLowerCase();
        if (['charging', 'session', 'plug_charge'].includes(type)) return 'Përfunduar';
        if (['cash_topup', 'cash_recharge', 'online_topup'].includes(type)) return 'Paguar';
      }
      return 'Në pritje';
    }
    const normalized = String(status).toLowerCase();
    if (normalized === 'paid') return 'Paguar';
    if (normalized === 'pending') return 'Në pritje';
    if (normalized === 'failed') return 'Dështuar';
    if (normalized === 'cancelled') return 'Anuluar';
    if (normalized === 'completed') return 'Përfunduar';
    if (normalized === 'stopped') return 'Ndalur';
    return status;
  };

  const getInvoiceDescription = (invoice) => {
    const type = String(invoice?.type || '').toLowerCase();
    if (type === 'charging') return `Sesion karikimi (${getInvoiceStatusLabel(invoice)})`;
    return invoice?.description || invoice?.type || '-';
  };

  const isCashTopupInvoice = (invoice) => {
    const mergedType = `${String(invoice?.type || '').toLowerCase()} ${String(invoice?.invoice_type || '').toLowerCase()}`;
    return (
      mergedType.includes('cash_topup') ||
      mergedType.includes('cash_recharge') ||
      mergedType.includes('online_topup') ||
      mergedType.includes('business_credit') ||
      mergedType.includes('business_employee_allocation')
    );
  };

  const getInvoiceCurrency = (invoice) => (isCashTopupInvoice(invoice) ? 'LEK' : 'Kredite');

  const getInvoiceStatusClass = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'paid') return 'bg-green-500/20 text-green-400';
    if (normalized === 'pending') return 'bg-yellow-500/20 text-yellow-400';
    if (normalized === 'failed' || normalized === 'cancelled') return 'bg-red-500/20 text-red-400';
    return 'bg-zinc-500/20 text-zinc-400';
  };

  const normalizeStatusLabel = (status) => {
    if (!status) return '-';
    const value = String(status).toLowerCase();
    if (value === 'completed') return 'Përfunduar';
    if (value === 'stopped') return 'Ndalur';
    if (value === 'active') return 'Aktiv';
    if (value === 'pending') return 'Në pritje';
    if (value === 'paid') return 'Paguar';
    if (value === 'failed') return 'Dështuar';
    return status;
  };

  const getBackPath = () => {
    return isModerator() ? '/moderator/clients' : '/clients';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-10 w-64" />
        <div className="skeleton h-96 w-full rounded-xl" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <User className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
        <p className="text-zinc-400">Klienti nuk u gjet</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(getBackPath())}>
          Kthehu
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(getBackPath())}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-blue-500/10 flex items-center justify-center">
              <User className="w-7 h-7 text-blue-500" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-white font-outfit">{client.full_name}</h1>
                {/* Verification Warning Badge */}
                {(() => {
                  const { isVerified, missing } = getVerificationStatus(client);
                  if (isVerified) return (
                    <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30">
                      <ShieldCheck className="w-3 h-3 mr-1" /> Verifikuar
                    </Badge>
                  );
                  return (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge 
                            variant="outline" 
                            className="bg-amber-500/10 text-amber-500 border-amber-500/20 cursor-help"
                          >
                            <AlertTriangle className="w-3 h-3 mr-1" /> Pa verifikuar
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs bg-zinc-900 border-zinc-700">
                          <div className="space-y-1">
                            <p className="font-medium text-amber-500">Dokumente që mungojnë:</p>
                            <ul className="text-xs space-y-1">
                              {missing.map((item, idx) => (
                                <li key={idx} className="flex items-center gap-1 text-zinc-300">
                                  <X className="w-3 h-3 text-red-400" /> {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })()}
              </div>
              <p className="text-zinc-400">{client.email}</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            className="bg-emerald-600/80 hover:bg-emerald-700"
            variant="default"
            onClick={() => setTopupDialogOpen(true)}
          >
            <Banknote className="w-4 h-4 mr-2" />
            Rimbush Cash
          </Button>
          <Button variant="outline" onClick={openEditProfile}>
            <Pencil className="w-4 h-4 mr-2" />
            Edito Profilin
          </Button>
          <Button variant="outline" onClick={() => setPasswordDialogOpen(true)}>
            <Key className="w-4 h-4 mr-2" />
            Ndrysho Fjalëkalimin
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Activity className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{client.total_sessions || 0}</p>
                <p className="text-sm text-zinc-400">Sesione</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Battery className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{(client.total_energy_kwh || 0).toFixed(1)}</p>
                <p className="text-sm text-zinc-400">kWh Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{(client.total_spent_lek || 0).toLocaleString()}</p>
                <p className="text-sm text-zinc-400">Kredite Shpenzuar</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{getWalletSummary(client).total.toLocaleString()}</p>
                <p className="text-sm text-zinc-400">Kredite Balanca Totale</p>
                <div className="mt-2 space-y-1 text-xs text-zinc-400">
                  <div className="flex items-center justify-between gap-3">
                    <span>Globale</span>
                    <span className="text-white">{getWalletSummary(client).globalBalance.toLocaleString()} Kredite</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span>Për stacione</span>
                    <span className="text-white">{getWalletSummary(client).stationBalance.toLocaleString()} Kredite</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Përmbledhje</TabsTrigger>
          <TabsTrigger value="documents">Dokumentat</TabsTrigger>
          <TabsTrigger value="sessions">Sesionet ({sessions.filter(s => ['completed','stopped','finished'].includes(String(s?.status||'').toLowerCase())).length || client?.total_sessions || 0})</TabsTrigger>
          <TabsTrigger value="transactions">Transaksionet</TabsTrigger>
          <TabsTrigger value="invoices">Faturat ({invoices.length || 0})</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 mt-4">

          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
                <Wallet className="w-4 h-4 text-emerald-500" /> Ndarja e Balancës
              </CardTitle>
              <CardDescription>
                Totali, balanca e përgjithshme dhe kreditë aktive sipas stacioneve.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-lg border border-white/10 bg-zinc-800/40 p-3">
                  <div className="text-xs text-zinc-500 uppercase">Totali</div>
                  <div className="text-lg font-semibold text-white mt-1">{getWalletSummary(client).total.toLocaleString()} Kredite</div>
                </div>
                <div className="rounded-lg border border-white/10 bg-zinc-800/40 p-3">
                  <div className="text-xs text-zinc-500 uppercase">Balanca e përgjithshme</div>
                  <div className="text-lg font-semibold text-white mt-1">{getWalletSummary(client).globalBalance.toLocaleString()} Kredite</div>
                </div>
                <div className="rounded-lg border border-white/10 bg-zinc-800/40 p-3">
                  <div className="text-xs text-zinc-500 uppercase">Balanca për stacione</div>
                  <div className="text-lg font-semibold text-white mt-1">{getWalletSummary(client).stationBalance.toLocaleString()} Kredite</div>
                </div>
              </div>

              <div>
                <div className="text-sm font-medium text-white mb-2">Kredite aktive sipas stacioneve</div>
                {getWalletSummary(client).restrictedWallets.length > 0 ? (
                  <div className="space-y-2">
                    {getWalletSummary(client).restrictedWallets.map((wallet, index) => (
                      <div key={`${wallet.site_id || wallet.moderator_id || 'station'}-${index}`} className="flex items-center justify-between rounded-lg border border-white/10 bg-zinc-800/40 p-3">
                        <div>
                          <div className="font-medium text-white">{wallet.site_name || wallet.site_id || 'Stacion i dedikuar'}</div>
                          <div className="text-xs text-zinc-500">Balancë e kufizuar për këtë stacion</div>
                        </div>
                        <div className="font-semibold text-emerald-400">{Number(wallet.balance || 0).toLocaleString()} Kredite</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-white/10 p-4 text-sm text-zinc-500">
                    Klienti nuk ka kredi aktive të lidhura me stacione specifike.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Personal Info Grid */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Informacione Personale</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                {client.id_card && (
                  <div>
                    <span className="text-muted-foreground">Nr. Kartës ID</span>
                    <p className="font-medium">{client.id_card}</p>
                  </div>
                )}
                {client.nipt && (
                  <div>
                    <span className="text-muted-foreground">NIPT</span>
                    <p className="font-medium">{client.nipt}</p>
                  </div>
                )}
                {client.country && (
                  <div>
                    <span className="text-muted-foreground">Shteti</span>
                    <p className="font-medium">{client.country}</p>
                  </div>
                )}
                {client.city && (
                  <div>
                    <span className="text-muted-foreground">Qyteti</span>
                    <p className="font-medium">{client.city}</p>
                  </div>
                )}
                {client.address && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Adresa</span>
                    <p className="font-medium">{client.address}</p>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">Login i Fundit</span>
                  <p className="font-medium">
                    {client.last_login
                      ? formatDate(client.last_login, { includeTime: true })
                      : 'Asnjëherë'
                    }
                  </p>
                </div>
                {client.last_login_method && (
                  <div>
                    <span className="text-muted-foreground">Metoda Login</span>
                    <p className="font-medium">
                      {client.last_login_method === 'face_id' ? 'Face ID' :
                       client.last_login_method === 'touch_id' ? 'Touch ID' : 'Email/Password'}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Zbritje Klienti</CardTitle>
              <CardDescription>
                Vendos zbritje me përqindje, me vlerë fikse në LEK/kWh, ose të dyja.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-[220px_220px_auto] gap-3 items-end">
                <div className="space-y-2">
                  <Label htmlFor="client-discount">Zbritja (%)</Label>
                  <Input
                    id="client-discount"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="p.sh. 10"
                    value={discountPercent}
                    onChange={(e) => setDiscountPercent(e.target.value)}
                    disabled={discountSaving}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client-fixed-discount">Zbritja fikse (Kredite/kWh)</Label>
                  <Input
                    id="client-fixed-discount"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="p.sh. 5"
                    value={discountFixedLek}
                    onChange={(e) => setDiscountFixedLek(e.target.value)}
                    disabled={discountSaving}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={handleSaveDiscount}
                    disabled={discountSaving}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    Ruaj zbritjen
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleClearDiscount}
                    disabled={discountSaving}
                  >
                    Hiq zbritjen
                  </Button>
                </div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Zbritja aktuale: {client?.charging_discount_percent ?? 0}%
                {client?.charging_discount_fixed_lek !== undefined && client?.charging_discount_fixed_lek !== null
                  ? ` • ${client.charging_discount_fixed_lek} Kredite/kWh`
                  : ''}
              </p>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2 pt-4 border-t">
            <Button
              variant="default"
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => setTopupDialogOpen(true)}
            >
              <Banknote className="w-4 h-4 mr-2" /> Rimbush Cash
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPasswordDialogOpen(true)}
            >
              <Key className="w-4 h-4 mr-2" /> Ndrysho Fjalëkalimin
            </Button>
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileText className="w-4 h-4" /> Dokumentat e Ngarkuara
              </CardTitle>
              <CardDescription>
                Dokumentat e identifikimit të klientit
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* ID Card */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <IdCard className="w-4 h-4" /> Karta ID
                    </span>
                    {client.id_card_document_url ? (
                      <Badge className="bg-green-500/20 text-green-500">Ngarkuar</Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-500">Mungon</Badge>
                    )}
                  </div>
                  {client.id_card_document_url ? (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api${client.id_card_document_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api${client.id_card_document_url}`}
                        alt="ID Card"
                        className="w-full h-32 object-cover rounded border cursor-pointer hover:opacity-80 transition"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling?.classList.remove('hidden');
                        }}
                      />
                      <div className="hidden w-full h-32 bg-muted rounded flex items-center justify-center text-sm text-muted-foreground">
                        Kliko për të hapur dokumentin
                      </div>
                    </a>
                  ) : (
                    <div className="w-full h-32 bg-muted rounded flex items-center justify-center">
                      <Image className="w-8 h-8 text-muted-foreground/50" />
                    </div>
                  )}
                  {client.id_card_verified && (
                    <Badge className="mt-2 bg-green-500/20 text-green-500">
                      <CheckCircle className="w-3 h-3 mr-1" /> Verifikuar
                    </Badge>
                  )}
                </div>

                {/* NIPT Document */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <FileText className="w-4 h-4" /> NIPT / Çertifikatë Biznesi
                    </span>
                    {client.nipt_document_url ? (
                      <Badge className="bg-green-500/20 text-green-500">Ngarkuar</Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-500">Mungon</Badge>
                    )}
                  </div>
                  {client.nipt_document_url ? (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api${client.nipt_document_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      {client.nipt_document_url.endsWith('.pdf') ? (
                        <div className="w-full h-32 bg-muted rounded flex items-center justify-center border hover:bg-muted/80 transition">
                          <div className="text-center">
                            <FileText className="w-8 h-8 mx-auto text-primary" />
                            <span className="text-sm text-muted-foreground mt-2">Kliko për PDF</span>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api${client.nipt_document_url}`}
                          alt="NIPT Document"
                          className="w-full h-32 object-cover rounded border cursor-pointer hover:opacity-80 transition"
                        />
                      )}
                    </a>
                  ) : (
                    <div className="w-full h-32 bg-muted rounded flex items-center justify-center">
                      <Image className="w-8 h-8 text-muted-foreground/50" />
                    </div>
                  )}
                  {client.nipt_verified && (
                    <Badge className="mt-2 bg-green-500/20 text-green-500">
                      <CheckCircle className="w-3 h-3 mr-1" /> Verifikuar
                    </Badge>
                  )}
                </div>
              </div>

              {/* Business Documents */}
              {client.client_type === 'business' && (
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <Building className="w-4 h-4" /> Dokumentat e Biznesit
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">NIPT</span>
                      <p className="font-medium">{client.nipt || 'Mungon'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Emri Biznesit</span>
                      <p className="font-medium">{client.business_name || client.full_name}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* No documents message */}
              {!client.id_card_document_url && !client.nipt_document_url && (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Ky klient nuk ka ngarkuar dokumente</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Activity className="w-4 h-4" /> Seancat e Karikimit
              </CardTitle>
              <CardDescription>
                {client.total_sessions || 0} seanca totale
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                const completedSessions = sessions.filter(s => ['completed','stopped','finished'].includes(String(s?.status||'').toLowerCase()));
                return completedSessions?.length > 0 ? (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {completedSessions.map((session) => (
                      <div key={session.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg text-sm hover:bg-muted transition">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                            <Zap className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{session.charger_id}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(session.start_time, { includeTime: true })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{(session.energy_kwh || 0).toFixed(2)} kWh</p>
                          <p className="text-sm text-emerald-500">
                            {formatCurrency(session.cost_lek || 0, '')} Kredite
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">{normalizeStatusLabel(session.status)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Activity className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Ky klient nuk ka seanca karikimi</p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Wallet className="w-4 h-4" /> Transaksionet
              </CardTitle>
              <CardDescription>
                Historiku i transaksioneve të portofolit
              </CardDescription>
            </CardHeader>
            <CardContent>
              {client.recent_transactions?.length > 0 ? (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {client.recent_transactions.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg text-sm hover:bg-muted transition">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          tx.type === 'cash_topup' || tx.type === 'cash_recharge' || tx.type === 'online_topup'
                            ? 'bg-emerald-500/20'
                            : 'bg-red-500/20'
                        }`}>
                          {tx.type === 'cash_topup' || tx.type === 'cash_recharge' || tx.type === 'online_topup' ? (
                            <TrendingUp className="w-5 h-5 text-emerald-500" />
                          ) : (
                            <Zap className="w-5 h-5 text-red-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{tx.description || tx.type}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(tx.created_at, { includeTime: true })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-medium ${
                          tx.type === 'cash_topup' || tx.type === 'cash_recharge' || tx.type === 'online_topup'
                            ? 'text-emerald-500'
                            : 'text-red-500'
                        }`}>
                          {tx.type === 'cash_topup' || tx.type === 'cash_recharge' || tx.type === 'online_topup' ? '+' : '-'}
                          {formatCurrency(tx.amount || 0, '')} Kredite
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Balancë: {formatCurrency(tx.balance_after || 0, '')} Kredite
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Wallet className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Nuk ka transaksione</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileText className="w-4 h-4" /> Faturat
              </CardTitle>
              <CardDescription>
                Faturat e klientit për rimbushje dhe karikime
              </CardDescription>
            </CardHeader>
            <CardContent>
              {invoices?.length > 0 ? (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {invoices.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg text-sm hover:bg-muted transition">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{inv.invoice_number}</p>
                          <p className="text-xs text-muted-foreground">
                            {(() => {
                              const mergedType = `${String(inv?.type || '').toLowerCase()} ${String(inv?.invoice_type || '').toLowerCase()}`;
                              const isCashTopup =
                                mergedType.includes('cash_topup') ||
                                mergedType.includes('cash_recharge') ||
                                mergedType.includes('online_topup') ||
                                mergedType.includes('business_credit') ||
                                mergedType.includes('business_employee_allocation');
                              return isCashTopup ? 'Rimbushje Cash' : `Statusi: ${getInvoiceStatusLabel(inv)}`;
                            })()}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {inv.license_plate ? `Targa: ${inv.license_plate}` : 'Pa targë'}
                            {inv.charger_id ? ` • Charger: ${inv.charger_id}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1">
                        <p className="font-medium text-emerald-500">
                          {formatCurrency(inv.amount || 0, getInvoiceCurrency(inv))}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(inv.created_at, { includeTime: false })}
                        </p>
                        <p className="text-xs text-muted-foreground">{getInvoiceStatusLabel(inv)}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1 min-w-[140px]">
                        <span className="text-[11px] text-muted-foreground uppercase tracking-wide">Veprime</span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 w-full"
                          onClick={() => handleViewClientInvoice(inv)}
                        >
                          <Eye className="w-3 h-3 mr-1" /> Shiko Faturën
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 w-full"
                          onClick={() => handleDownloadClientInvoice(inv)}
                        >
                          <Download className="w-3 h-3 mr-1" /> Shkarko PDF
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Nuk ka fatura</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

        <InvoicePopup
          session={invoicePreviewOpen ? selectedInvoice : null}
          onClose={() => {
            setInvoicePreviewOpen(false);
            setSelectedInvoice(null);
          }}
        />

      {/* Cash Topup Dialog */}
      <Dialog open={topupDialogOpen} onOpenChange={setTopupDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Banknote className="w-5 h-5 text-emerald-500" />
              Rimbush Cash
            </DialogTitle>
            <DialogDescription>
              Shto balancë për {client.full_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-zinc-800 rounded-lg border border-zinc-700">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-400">Balanca aktuale:</span>
                <span className="font-bold text-emerald-500">
                  {(client.wallet_balance || 0).toLocaleString()} Kredite
                </span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Stacioni</Label>
              <Select value={topupSiteId} onValueChange={setTopupSiteId}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="topup-site-select">
                  <SelectValue placeholder="Zgjidh stacionin" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name || site.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500">Balanca cash përdoret vetëm në këtë stacion.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Shuma (LEK)</Label>
              <Input
                type="number"
                placeholder="p.sh. 500"
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                min="1"
                className="bg-zinc-800 border-zinc-700"
                data-testid="topup-amount-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Shënime (opsionale)</Label>
              <Input
                type="text"
                placeholder="p.sh. Pagesë cash në zyrë"
                value={topupNotes}
                onChange={(e) => setTopupNotes(e.target.value)}
                className="bg-zinc-800 border-zinc-700"
                data-testid="topup-notes-input"
              />
            </div>

            {topupAmount && parseFloat(topupAmount) > 0 && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-300">Balanca totale e re:</span>
                  <span className="font-bold text-emerald-500">
                    {((client.wallet_balance || 0) + parseFloat(topupAmount)).toLocaleString()} Kredite
                  </span>
                </div>
                <p className="text-xs text-emerald-300 mt-2">
                  Krediti lidhet me: {sites.find((site) => site.id === topupSiteId)?.name || 'stacionin e zgjedhur'}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTopupDialogOpen(false)}
              disabled={topupLoading}
            >
              Anulo
            </Button>
            <Button
              onClick={handleCashTopup}
              disabled={topupLoading || !topupAmount || parseFloat(topupAmount) <= 0 || !topupSiteId}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {topupLoading ? 'Duke procesuar...' : 'Konfirmo Mbushjen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={editProfileOpen} onOpenChange={setEditProfileOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              Edito Profilin
            </DialogTitle>
            <DialogDescription>Përditëso të dhënat e klientit {client.full_name}</DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            {/* Basic Info */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Emri i Plotë</Label>
              <Input
                value={editProfileForm.full_name}
                onChange={(e) => setEditProfileForm(prev => ({ ...prev, full_name: e.target.value }))}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Emri dhe Mbiemri"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Telefoni</Label>
              <Input
                value={editProfileForm.phone}
                onChange={(e) => setEditProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                className="bg-zinc-800 border-zinc-700"
                placeholder="+355 6X XXX XXXX"
              />
            </div>
            
            {/* Address Fields */}
            <div className="col-span-2 border-t border-zinc-700 pt-4 mt-2">
              <h4 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Adresa (KYC)
              </h4>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Shteti <span className="text-red-400">*</span></Label>
              <Input
                value={editProfileForm.country}
                onChange={(e) => setEditProfileForm(prev => ({ ...prev, country: e.target.value }))}
                className="bg-zinc-800 border-zinc-700"
                placeholder="p.sh. Shqipëri"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Qyteti <span className="text-red-400">*</span></Label>
              <Input
                value={editProfileForm.city}
                onChange={(e) => setEditProfileForm(prev => ({ ...prev, city: e.target.value }))}
                className="bg-zinc-800 border-zinc-700"
                placeholder="p.sh. Tiranë"
              />
            </div>
            
            <div className="space-y-2 col-span-2">
              <Label className="text-zinc-300">Adresa e Plotë <span className="text-red-400">*</span></Label>
              <Input
                value={editProfileForm.address}
                onChange={(e) => setEditProfileForm(prev => ({ ...prev, address: e.target.value }))}
                className="bg-zinc-800 border-zinc-700"
                placeholder="p.sh. Rruga Myslym Shyri, Nr. 10, Tiranë"
              />
            </div>
            
            {/* ID / Business Fields */}
            <div className="col-span-2 border-t border-zinc-700 pt-4 mt-2">
              <h4 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
                <IdCard className="w-4 h-4" /> Identifikimi
              </h4>
            </div>
            
            {client.client_type === 'business' ? (
              <>
                <div className="space-y-2">
                  <Label className="text-zinc-300">NIPT <span className="text-red-400">*</span></Label>
                  <Input
                    value={editProfileForm.nipt}
                    onChange={(e) => setEditProfileForm(prev => ({ ...prev, nipt: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="p.sh. L91234567A"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Emri i Biznesit</Label>
                  <Input
                    value={editProfileForm.business_name}
                    onChange={(e) => setEditProfileForm(prev => ({ ...prev, business_name: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="p.sh. Kompania XYZ sh.p.k."
                  />
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label className="text-zinc-300">Numri i Kartës së Identitetit <span className="text-red-400">*</span></Label>
                <Input
                  value={editProfileForm.id_card}
                  onChange={(e) => setEditProfileForm(prev => ({ ...prev, id_card: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="p.sh. I12345678A"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditProfileOpen(false)}
              disabled={editProfileLoading}
            >
              Anulo
            </Button>
            <Button
              onClick={handleSaveProfile}
              disabled={editProfileLoading}
              className="bg-primary hover:bg-primary/90"
            >
              {editProfileLoading ? 'Duke ruajtur...' : 'Ruaj Ndryshimet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Ndrysho Fjalëkalimin</DialogTitle>
            <DialogDescription>{client.full_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Fjalëkalimi i Ri</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 karaktere"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>Anulo</Button>
            <Button onClick={handleResetPassword}>Ndrysho</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vehicle Detail Dialog */}
      <Dialog open={vehicleDetailOpen} onOpenChange={setVehicleDetailOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-3">
              {selectedVehicle?.photo_url && selectedVehicle.photo_url.toLowerCase().endsWith('.png') ? (
                <img 
                  src={selectedVehicle.photo_url} 
                  alt={`${selectedVehicle?.brand} ${selectedVehicle?.model}`}
                  className="w-16 h-16 object-cover rounded-lg"
                />
              ) : (
                <div className="w-16 h-16 bg-zinc-800 rounded-lg flex items-center justify-center">
                  <Car className="w-8 h-8 text-emerald-500" />
                </div>
              )}
              <div>
                <div>{selectedVehicle?.brand} {selectedVehicle?.model}</div>
                <div className="text-sm text-zinc-400 font-normal">{selectedVehicle?.year}</div>
              </div>
            </DialogTitle>
            <DialogDescription>
              Profili i detajuar i automjetit dhe historiku i karikimeve
            </DialogDescription>
          </DialogHeader>
          
          {selectedVehicle && (
            <div className="space-y-6 mt-4">
              {/* Vehicle Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Targa</p>
                  <p className="text-lg font-mono text-emerald-400">{selectedVehicle.license_plate || '-'}</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Sesione Totale</p>
                  <p className="text-lg text-white">{selectedVehicle.session_count || 0}</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Energji Totale</p>
                  <p className="text-lg text-yellow-400">{(selectedVehicle.total_energy || 0).toFixed(2)} kWh</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Shpenzime</p>
                  <p className="text-lg text-blue-400">{(selectedVehicle.total_cost || 0).toLocaleString()} Kredite</p>
                </div>
              </div>

              {/* Additional Vehicle Details */}
              {(selectedVehicle.color || selectedVehicle.vin || selectedVehicle.battery_capacity) && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {selectedVehicle.color && (
                    <div className="bg-zinc-800/30 rounded-lg p-3">
                      <p className="text-xs text-zinc-500">Ngjyra</p>
                      <p className="text-sm text-zinc-300">{selectedVehicle.color}</p>
                    </div>
                  )}
                  {selectedVehicle.vin && (
                    <div className="bg-zinc-800/30 rounded-lg p-3">
                      <p className="text-xs text-zinc-500">VIN</p>
                      <p className="text-sm text-zinc-300 font-mono">{selectedVehicle.vin}</p>
                    </div>
                  )}
                  {selectedVehicle.battery_capacity && (
                    <div className="bg-zinc-800/30 rounded-lg p-3">
                      <p className="text-xs text-zinc-500">Kapaciteti Baterisë</p>
                      <p className="text-sm text-zinc-300">{selectedVehicle.battery_capacity} kWh</p>
                    </div>
                  )}
                </div>
              )}

              {/* Charging History */}
              <div>
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-blue-400" />
                  Historiku i Karikimeve
                </h4>
                
                {vehicleHistoryLoading ? (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-zinc-500 text-sm">Duke ngarkuar...</p>
                  </div>
                ) : vehicleHistory.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500 bg-zinc-800/30 rounded-lg">
                    <Zap className="w-8 h-8 mx-auto mb-2 text-zinc-600" />
                    <p>Nuk ka sesione karikimi për këtë automjet</p>
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10">
                          <TableHead className="text-zinc-400">Data</TableHead>
                          <TableHead className="text-zinc-400">Karikues</TableHead>
                          <TableHead className="text-zinc-400">Kohëzgjatja</TableHead>
                          <TableHead className="text-zinc-400">Energji</TableHead>
                          <TableHead className="text-zinc-400">Çmimi</TableHead>
                          <TableHead className="text-zinc-400">Statusi</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {vehicleHistory.map((session) => (
                          <TableRow key={session.id} className="border-white/5">
                            <TableCell className="text-white text-sm">
                              {formatDate(session.start_time)}
                            </TableCell>
                            <TableCell className="text-zinc-300 font-mono text-sm">
                              {session.charger_name || session.charger_id?.substring(0, 8) || '-'}
                            </TableCell>
                            <TableCell className="text-zinc-400 text-sm">
                              {session.duration_minutes 
                                ? `${Math.floor(session.duration_minutes / 60)}h ${session.duration_minutes % 60}m` 
                                : '-'}
                            </TableCell>
                            <TableCell className="text-yellow-400 text-sm">
                              {(session.energy_consumed || 0).toFixed(2)} kWh
                            </TableCell>
                            <TableCell className="text-blue-400 text-sm">
                              {(session.total_cost || 0).toLocaleString()} Kredite
                            </TableCell>
                            <TableCell>
                              <span className={`inline-flex px-2 py-0.5 rounded text-xs ${
                                session.status === 'completed' 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : session.status === 'in_progress'
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : 'bg-zinc-500/20 text-zinc-400'
                              }`}>
                                {session.status === 'completed' ? 'Përfunduar' 
                                  : session.status === 'in_progress' ? 'Aktiv' 
                                  : session.status}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setVehicleDetailOpen(false)}>Mbyll</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Document Preview Modal */}
      <Dialog open={documentPreviewOpen} onOpenChange={setDocumentPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              {previewDocumentTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-zinc-800 rounded-lg p-2 min-h-[400px] max-h-[70vh]">
            {previewDocumentUrl && (
              previewDocumentUrl.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={previewDocumentUrl}
                  className="w-full h-full min-h-[500px] rounded"
                  title={previewDocumentTitle}
                />
              ) : (
                <img
                  src={previewDocumentUrl}
                  alt={previewDocumentTitle}
                  className="max-w-full h-auto mx-auto rounded"
                  style={{ maxHeight: '70vh' }}
                />
              )
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => window.open(previewDocumentUrl, '_blank')}
              className="gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Hap në tab të ri
            </Button>
            <Button variant="outline" onClick={() => setDocumentPreviewOpen(false)}>
              Mbyll
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDetail;
